import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Chip, 
  Divider, 
  Card, 
  CardMedia, 
  CardContent, 
  Avatar, 
  AvatarGroup, 
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CalendarMonth as CalendarIcon,
  Place as PlaceIcon,
  Group as GroupIcon,
  Photo as PhotoIcon,
  Comment as CommentIcon,
  MoreVert as MoreIcon,
  KeyboardArrowRight as ArrowRightIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

type FirebaseTimestamp = {
  toDate: () => Date;
};

type DateLike = Date | FirebaseTimestamp | string;

interface HistoricalTrip {
  id: string;
  title: string;
  description?: string;
  startDate: DateLike;
  endDate: DateLike;
  primaryDestination?: {
    name: string;
    coordinates?: [number, number];
  };
  coverPhotoUrl?: string;
  coverImageUrl?: string;
  photoCount?: number;
  collaborators?: Array<{
    id: string;
    name: string;
    avatar?: string;
    role?: string;
  }>;
  commentCount?: number;
}

interface HistoricalTimelineProps {
  trips: HistoricalTrip[];
  onTripSelect: (tripId: string) => void;
  onViewGallery?: (tripId: string) => void;
}

// Helper function to safely convert different date formats with better error handling
const safelyGetDate = (date: DateLike): Date => {
  try {
    if (date instanceof Date) {
      return date;
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      return date.toDate();
    } else if (typeof date === 'string') {
      return new Date(date);
    } else if (date && typeof date === 'object') {
      // Try to handle cases where date might have a different structure
      if ('seconds' in (date as any) && 'nanoseconds' in (date as any)) {
        // Handle Firestore timestamp format
        return new Date((date as any).seconds * 1000);
      }
    }
    
    console.warn("Could not parse date, using current date as fallback");
    return new Date(); // Fallback
  } catch (error) {
    console.error("Error parsing date:", error);
    return new Date(); // Fallback on error
  }
};

// Add a safe format function to handle date formatting with error handling
const safeFormat = (date: DateLike, formatStr: string): string => {
  try {
    const parsedDate = safelyGetDate(date);
    return format(parsedDate, formatStr);
  } catch (error) {
    console.error("Error formatting date:", error, date);
    return "Invalid date";
  }
};

const HistoricalTimeline: React.FC<HistoricalTimelineProps> = ({ 
  trips, 
  onTripSelect,
  onViewGallery
}) => {
  const navigate = useNavigate();


  
  // Skip the complex sorting and grouping for now to ensure basic rendering works
  // Display trips in a simple grid
  return (
    <Box sx={{ width: '100%', mt: 2, p: 1, borderRadius: 1, backgroundColor: '#fafafa' }}>
      <Typography variant="h5" gutterBottom>
        Your Past Trips ({trips.length})
      </Typography>
      
      {trips.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No trips found
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Add your past travel experiences to build your personal travel history.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/trip-planner')}
          >
            Add Your First Trip
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {trips.map(trip => (
            <Grid item xs={12} md={6} lg={4} key={trip.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  backgroundColor: '#fff',
                  boxShadow: 1,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}
              >
                {(trip.coverPhotoUrl || trip.coverImageUrl) ? (
                  <CardMedia
                    component="img"
                    height="140"
                    image={trip.coverPhotoUrl || trip.coverImageUrl}
                    alt={trip.title}
                    sx={{ objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Trip';
                    }}
                  />
                ) : (
                  <Box 
                    sx={{ 
                      height: 140, 
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="h6" color="white">
                      {trip.title}
                    </Typography>
                  </Box>
                )}
                
                <CardContent>
                  <Typography variant="h6" component="div" gutterBottom>
                    {trip.title}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {safeFormat(trip.startDate, 'MMM d')} - {safeFormat(trip.endDate, 'MMM d, yyyy')}
                    </Typography>
                  </Box>
                  
                  {trip.primaryDestination && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PlaceIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {trip.primaryDestination.name}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                      size="small" 
                      onClick={() => onViewGallery && onViewGallery(trip.id)}
                      startIcon={<PhotoIcon />}
                      variant="outlined"
                      color="primary"
                    >
                      Photos
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => onTripSelect(trip.id)}
                      variant="contained"
                      color="primary"
                    >
                      View Trip
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default HistoricalTimeline; 